import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  AttachMoney as MoneyIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getAssociateLedger, getAssociates } from '../../services/associate';
import {
  getBookings,
  getProjectsPaginated,
  getPlotsByProjectSearch,
} from '../../services/project';
import { getLeadInterested } from '../../services/master';
import { useHistory } from 'react-router-dom';
import AlertModal from '../../components/AlertModal';
import {
  exportAsExcel,
  exportAsCSV,
  exportAsPDF,
} from '../../utils/importExport';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const PaymentAssociateList = () => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    unitType: '',
    project: '',
    unitNo: '',
    bookingNo: null,
    associateName: null,
    includeTeamMembers: 'Yes',
  });
  const [paymentData, setPaymentData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [apiSummary, setApiSummary] = useState({
    totalCommission: 0,
    totalPayableCommission: 0,
    totalPaidCommission: 0,
    dueCommission: 0,
  });
  const [loading, setLoading] = useState(false);
  const [associateOptions, setAssociateOptions] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingOptions, setBookingOptions] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingSearchInput, setBookingSearchInput] = useState('');
  const [associatesLoaded, setAssociatesLoaded] = useState(false);
  const [bookingSearchedFirst, setBookingSearchedFirst] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);

  // Project dropdown state
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [projectPage, setProjectPage] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const projectSearchTimeoutRef = React.useRef(null);
  const isMounted = React.useRef(false);
  const isResetting = React.useRef(false);

  // Unit type state
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState(false);

  // Unit dropdown state
  const [unitOptions, setUnitOptions] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitSearchInput, setUnitSearchInput] = useState('');
  const [unitPage, setUnitPage] = useState(1);
  const [hasMoreUnits, setHasMoreUnits] = useState(true);
  const unitSearchTimeoutRef = React.useRef(null);

  // Modal state
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    title: 'Alert',
    message: '',
    type: 'info',
  });

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };

  const includeTeamOptions = ['Yes', 'No'];

  // Sample table data
  const sampleData = [];

  // Summary calculations from API data
  const calculateSummary = () => {
    if (paymentData.length === 0) {
      return {
        cashReceived: 0,
        paymentAdvance: 0,
        commissionPaid: 0,
        adjustment: 0,
      };
    }

    // Calculate based on raw data from API
    const cashReceived = paymentData
      .filter((item) => item._raw?.paymentMode === 'Cash')
      .reduce((sum, item) => sum + (item._raw?.netAmount || 0), 0);

    const paymentAdvance = paymentData
      .filter(
        (item) =>
          (item._raw?.paymentHead || item._raw?.head) === 'Advance Payment',
      )
      .reduce((sum, item) => sum + (item._raw?.amount || 0), 0);

    const commissionPaid = paymentData
      .filter(
        (item) =>
          (item._raw?.paymentHead || item._raw?.head) === 'Commission Paid' ||
          (!(item._raw?.paymentHead || item._raw?.head) &&
            item._raw?.transactionType === 'add'),
      )
      .reduce((sum, item) => sum + (item._raw?.amount || 0), 0);

    const adjustment = paymentData.reduce(
      (sum, item) => sum + (item._raw?.charge || 0),
      0,
    );

    return {
      cashReceived,
      paymentAdvance,
      commissionPaid,
      adjustment,
    };
  };

  const summary = calculateSummary();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (selectedAssociate || selectedBooking || filters.project || filters.unitType || filters.unitNo || filters.fromDate || filters.toDate) {
      handleSearch(newPage, rowsPerPage);
    } else {
      fetchPaymentData(newPage + 1, rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    if (selectedAssociate || selectedBooking || filters.project || filters.unitType || filters.unitNo || filters.fromDate || filters.toDate) {
      handleSearch(0, newRows);
    } else {
      fetchPaymentData(1, newRows);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Fetch associate ledger data on component mount
  const fetchPaymentData = useCallback(async (pageNum = 1, limit = rowsPerPage) => {
    setLoading(true);
    try {
      const response = await getAssociateLedger({ page: pageNum, limit });
      console.log('Associate ledger data:', response);

      if (response.success && Array.isArray(response.data)) {
        // Capture summary from API
        if (response.summary) {
          setApiSummary({
            totalCommission: response.summary.totalCommission || 0,
            totalPayableCommission: response.summary.totalPayableCommission || 0,
            totalPaidCommission: response.summary.totalPaidCommission || 0,
            dueCommission: response.summary.dueCommission || 0,
          });
        }
        // Capture total count for pagination
        const total =
          response.pagination?.total ||
          response.pagination?.totalDocs ||
          response.extra?.total ||
          response.totalCount ||
          response.data.length;
        setTotalCount(Number(total) || 0);

        // Map API response to table format
        const mappedData = response.data.map((item) => ({
          id: item._id,
          date: item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : '',
          uptoDate: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-GB') : '',
          associate: item.bookingInfo?.associate?.name || item.associateDetails?.name || '',
          associatePhone: item.bookingInfo?.associate?.phone || item.associateDetails?.phone || '',
          payableAmount: typeof item.netAmount === 'number' ? item.netAmount : (typeof item.amount === 'number' ? item.amount : 0),
          project: item.bookingInfo?.particular?.projectName || '',
          projectId: item.projectId || item.bookingInfo?.particular?.projectId || '',
          unit: (item.bookingInfo?.particular?.inventoryDetail || item.inventoryName || '').trim(),
          head: item.paymentHead || item.head || item.description || item.particular || '',
          bookingId: item.bookingId || '',
          bookingIdNo: item.bookingIdNo || item.bookingInfo?.bookingId || '',
          _raw: item,
        }));
        setPaymentData(mappedData);
      } else {
        setPaymentData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
      showAlert('Failed to fetch payment data. Please try again later.', 'error', 'Error');
      setPaymentData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      isResetting.current = false;
    }
  }, [rowsPerPage]);

  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  const handleReset = () => {
    isResetting.current = true;
    setFilters({
      fromDate: '',
      toDate: '',
      unitType: '',
      project: '',
      unitNo: '',
      bookingNo: null,
      associateName: null,
      includeTeamMembers: 'Yes',
    });
    setAssociateSearchInput('');
    setSelectedAssociate(null);
    setSelectedBooking(null);
    setSelectedProject(null);
    setSelectedUnit(null);
    setBookingSearchInput('');
    setBookingSearchedFirst(false);
    setProjectSearchInput('');
    setProjectPage(1);
    setUnitSearchInput('');
    setUnitPage(1);
    setPage(0);
    setBookingOptions([]);
    fetchPaymentData(1, rowsPerPage);
  };

  const setUnitNo = (val) => handleFilterChange('unitNo', val);

  // Fetch unit numbers based on project and unit type
  const fetchUnits = useCallback(
    async (search = '', pageNum = 1, isAppend = false) => {
      const projectId = filters.project || '';
      const unitType = filters.unitType || '';

      if (!projectId) {
        setUnitOptions([]);
        return;
      }

      if (pageNum === 1) {
        setLoadingUnits(true);
      }

      try {
        const response = await getPlotsByProjectSearch(
          projectId,
          unitType,
          null,
          null,
          search,
          pageNum,
          20,
        );

        const inventories = response.inventories || [];

        // Format inventories
        const formattedUnits = inventories.map((unit) => ({
          id: unit._id || unit.id,
          unitNo:
            (unit.plotName || unit.unitNo || unit.flatNo || unit.name || 'N/A').trim(),
        }));

        if (isAppend) {
          setUnitOptions((prev) => [...prev, ...formattedUnits]);
        } else {
          setUnitOptions(formattedUnits);
        }

        setHasMoreUnits(formattedUnits.length === 20);
        setUnitPage(pageNum);
      } catch (error) {
        console.error('Failed to fetch units:', error);
      } finally {
        setLoadingUnits(false);
      }
    },
    [filters.project, filters.unitType],
  );

  // Handle unit search input change
  useEffect(() => {
    if (unitSearchInput.length === 0 && unitOptions.length > 0) return;

    if (unitSearchTimeoutRef.current) {
      clearTimeout(unitSearchTimeoutRef.current);
    }

    unitSearchTimeoutRef.current = setTimeout(() => {
      fetchUnits(unitSearchInput, 1, false);
    }, 500);

    return () => clearTimeout(unitSearchTimeoutRef.current);
  }, [unitSearchInput, fetchUnits]);

  // Refetch units when project or unit type changes
  useEffect(() => {
    handleFilterChange('unitNo', '');
    setSelectedUnit(null);
    setUnitOptions([]);
    setUnitPage(1);
    setHasMoreUnits(true);
    if (filters.project) {
      fetchUnits('', 1, false);
    }
  }, [filters.project, filters.unitType]);

  // Fetch projects with pagination and search
  const fetchProjects = useCallback(
    async (search = '', pageNum = 1, isAppend = false) => {
      if (pageNum === 1) {
        setLoadingProjects(true);
      }

      try {
        const params = {
          page: pageNum,
          limit: 10,
          search: search,
        };

        const response = await getProjectsPaginated(params);

        let projectsArr = [];
        if (response.status && Array.isArray(response.data)) {
          projectsArr = response.data;
        } else if (response.success && Array.isArray(response.data)) {
          projectsArr = response.data;
        } else if (Array.isArray(response.data?.docs)) {
          projectsArr = response.data.docs;
        }

        const formattedProjects = projectsArr.map((project) => ({
          id: project._id || project.id,
          projectName: project.projectName || '',
        }));

        if (isAppend) {
          setProjectOptions((prev) => [...prev, ...formattedProjects]);
        } else {
          setProjectOptions(formattedProjects);
        }

        setHasMoreProjects(formattedProjects.length === 10);
        setProjectPage(pageNum);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    },
    [],
  );

  // Handle project search input change
  useEffect(() => {
    if (projectSearchInput.length === 0 && projectOptions.length > 0) return;

    if (projectSearchTimeoutRef.current) {
      clearTimeout(projectSearchTimeoutRef.current);
    }

    projectSearchTimeoutRef.current = setTimeout(() => {
      fetchProjects(projectSearchInput, 1, false);
    }, 500);

    return () => clearTimeout(projectSearchTimeoutRef.current);
  }, [projectSearchInput, fetchProjects]);

  // Fetch unit types (project types)
  const fetchUnitTypes = useCallback(async () => {
    try {
      setLoadingUnitTypes(true);
      const data = await getLeadInterested();
      const filteredTypes = data
        .filter((item) => item.rawStatus)
        .map((item) => item.leadInterested);
      setUnitTypeOptions(filteredTypes);
    } catch (error) {
      console.error('Failed to fetch unit types:', error);
    } finally {
      setLoadingUnitTypes(false);
    }
  }, []);

  useEffect(() => {
    fetchUnitTypes();
  }, [fetchUnitTypes]);

  // Fetch associates only when dropdown is opened
  const fetchAssociates = async (searchTerm = '') => {
    // CRITICAL: Don't call associate API if booking was searched first
    // Associate data comes from booking response, no need to call associate API
    if (bookingSearchedFirst) {
      return;
    }

    setLoadingAssociates(true);
    try {
      const params = {
        page: 1,
        limit: 20,
      };

      if (searchTerm && searchTerm.trim().length > 0) {
        params.search = searchTerm.trim();
      }

      const response = await getAssociates(params);

      if (response.status && Array.isArray(response.data)) {
        const formattedAssociates = response.data.map((associate) => ({
          id: associate._id,
          name: associate.name || '',
          phone: associate.phone || '',
          associateId: associate.associateId || associate._id || '',
        }));
        setAssociateOptions(formattedAssociates);
        setAssociatesLoaded(true);
      } else {
        setAssociateOptions([]);
      }
    } catch (error) {
      console.error('Failed to fetch associates:', error);
      setAssociateOptions([]);
    } finally {
      setLoadingAssociates(false);
    }
  };

  // Debounced search for associates - only if booking wasn't searched first
  useEffect(() => {
    // CRITICAL: Don't call associate API at all if booking was searched first
    // (associate comes from booking response, no need to call associate API)
    if (bookingSearchedFirst) {
      return; // Completely skip associate API calls
    }

    if (associateSearchInput.trim().length < 2 && associatesLoaded) {
      return; // Don't search if input is too short and we already have data
    }

    const timeoutId = setTimeout(() => {
      // Only search if user manually types in associate field (length >= 2)
      // This resets the bookingSearchedFirst flag
      if (associateSearchInput.trim().length >= 2) {
        setBookingSearchedFirst(false); // Reset flag when user manually searches associate
        fetchAssociates(associateSearchInput);
      } else if (!associatesLoaded) {
        // Only auto-load if booking wasn't searched first
        fetchAssociates('');
      }
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [associateSearchInput, bookingSearchedFirst, associatesLoaded]);

  // Fetch bookings when bookingNo is typed (with debouncing)
  useEffect(() => {
    const fetchBookingsByBookingId = async () => {
      if (!bookingSearchInput || bookingSearchInput.trim().length === 0) {
        setBookingOptions([]);
        setBookingSearchedFirst(false);
        // Clear associate if booking input is cleared
        if (bookingSearchedFirst) {
          setSelectedAssociate(null);
          setAssociateOptions([]);
          handleFilterChange('associateName', '');
        }
        return;
      }

      // CRITICAL: Don't call API if booking is already selected and input matches the booking ID or display label
      // This prevents API calls when dropdown opens and shows the display label
      if (selectedBooking && selectedBooking.bookingId) {
        const inputValue = bookingSearchInput.trim();
        const bookingIdStr = selectedBooking.bookingId.toString();
        // If input matches the booking ID or is the display label, don't call API
        // Just update the input to the booking ID if it's the display label
        if (inputValue === selectedBooking.displayLabel) {
          setBookingSearchInput(bookingIdStr); // Update to booking ID, don't call API
          return;
        }
        if (inputValue === bookingIdStr) {
          return; // Booking already selected, no need to call API
        }
      }

      setLoadingBookings(true);
      setBookingSearchedFirst(true); // Mark that booking was searched first
      try {
        const params = {
          page: 1,
          limit: 1000,
          bookingId: bookingSearchInput.trim(),
        };

        const response = await getBookings(params);

        if (response.success && Array.isArray(response.data)) {
          const formattedBookings = response.data.map((booking) => ({
            id: booking._id,
            bookingId: booking.bookingId || '',
            projectName: booking.particular?.projectName || '',
            inventoryDetail: booking.particular?.inventoryDetail || '',
            associate: booking.associate || null,
            displayLabel: `Booking ${booking.bookingId || ''} - ${booking.particular?.projectName || ''}`,
            rawData: booking,
          }));
          setBookingOptions(formattedBookings);

          // Auto-populate associate and set selected booking if booking is found
          if (formattedBookings.length > 0) {
            const booking = formattedBookings[0];
            setSelectedBooking(booking);
            handleFilterChange('bookingNo', booking.bookingId);
            // Set bookingSearchInput to the booking ID (not display label) to prevent API calls on dropdown open
            setBookingSearchInput(booking.bookingId.toString());

            // Auto-populate associate from booking response (NO associate API call)
            if (booking.associate) {
              const associate = booking.associate;
              // Use associateId as the MongoDB _id for API calls
              const associateOption = {
                id: associate.associateId || '', // MongoDB _id from booking response
                name: associate.name || '',
                phone: associate.phone || '',
                associateId: associate.associateId || '',
              };
              setSelectedAssociate(associateOption);
              setAssociateOptions([associateOption]); // Set only this associate, don't call API
              handleFilterChange('associateName', associateOption.name);
            }
          } else {
            // No booking found, clear associate
            setSelectedAssociate(null);
            setAssociateOptions([]);
            handleFilterChange('associateName', '');
          }
        } else {
          setBookingOptions([]);
          setSelectedAssociate(null);
          setAssociateOptions([]);
          handleFilterChange('associateName', '');
        }
      } catch (error) {
        console.error('Failed to fetch bookings:', error);
        setBookingOptions([]);
        setSelectedAssociate(null);
        setAssociateOptions([]);
        handleFilterChange('associateName', '');
      } finally {
        setLoadingBookings(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchBookingsByBookingId();
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [bookingSearchInput]);

  // Fetch bookings when associate is selected first (not from booking search)
  useEffect(() => {
    // Only fetch bookings if associate was selected manually (not from booking search)
    if (bookingSearchedFirst) {
      return; // Don't fetch bookings if booking was searched first
    }

    const fetchBookingsByAssociateId = async () => {
      if (!selectedAssociate || !selectedAssociate.id) {
        setBookingOptions([]);
        setSelectedBooking(null);
        setBookingSearchInput('');
        return;
      }

      setLoadingBookings(true);
      try {
        const params = {
          page: 1,
          limit: 1000,
          associateId: selectedAssociate.id,
        };

        const response = await getBookings(params);

        if (response.success && Array.isArray(response.data)) {
          const formattedBookings = response.data.map((booking) => ({
            id: booking._id,
            bookingId: booking.bookingId || '',
            projectName: booking.particular?.projectName || '',
            inventoryDetail: booking.particular?.inventoryDetail || '',
            associate: booking.associate || null,
            displayLabel: `Booking ${booking.bookingId || ''} - ${booking.particular?.projectName || ''}`,
            rawData: booking,
          }));
          setBookingOptions(formattedBookings);
        } else {
          setBookingOptions([]);
        }
      } catch (error) {
        console.error('Failed to fetch bookings:', error);
        setBookingOptions([]);
      } finally {
        setLoadingBookings(false);
      }
    };

    fetchBookingsByAssociateId();
  }, [selectedAssociate, bookingSearchedFirst]);

  const handleSearch = async (pageOverride, limitOverride) => {
    const pageNum = pageOverride !== undefined ? pageOverride + 1 : 1;
    const limit   = limitOverride !== undefined ? limitOverride : rowsPerPage;
    if (pageOverride === undefined) setPage(0);
    setLoading(true);
    try {
      const params = { page: pageNum, limit };

      if (filters.includeTeamMembers === 'Yes') {
        if (selectedBooking && selectedBooking.id) {
          params.bookingId = selectedBooking.id;
        } else if (selectedAssociate && selectedAssociate.id) {
          params.associateId = selectedAssociate.id;
        }
      } else {
        if (selectedAssociate && selectedAssociate.id) params.associateId = selectedAssociate.id;
        if (selectedBooking && selectedBooking.id) params.bookingId = selectedBooking.id;
      }

      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate)   params.toDate   = filters.toDate;
      if (filters.project)  params.projectId = filters.project;
      if (filters.unitType) params.unitType  = filters.unitType;
      if (filters.unitNo)   params.unitId    = filters.unitNo.trim();

      const response = await getAssociateLedger(params);
      console.log('Associate ledger search response:', response);

      if (response.success && Array.isArray(response.data)) {
        if (response.summary) {
          setApiSummary({
            totalCommission: response.summary.totalCommission || 0,
            totalPayableCommission: response.summary.totalPayableCommission || 0,
            totalPaidCommission: response.summary.totalPaidCommission || 0,
            dueCommission: response.summary.dueCommission || 0,
          });
        }
        const total =
          response.pagination?.total ||
          response.pagination?.totalDocs ||
          response.extra?.total ||
          response.totalCount ||
          response.data.length;
        setTotalCount(Number(total) || 0);

        const mappedData = response.data.map((item) => ({
          id: item._id,
          date: item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : '',
          uptoDate: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-GB') : '',
          associate: item.bookingInfo?.associate?.name || item.associateDetails?.name || '',
          associatePhone: item.bookingInfo?.associate?.phone || item.associateDetails?.phone || '',
          payableAmount: typeof item.netAmount === 'number' ? item.netAmount : (typeof item.amount === 'number' ? item.amount : 0),
          head: item.paymentHead || item.head || item.description || item.particular || '',
          project: item.bookingInfo?.particular?.projectName || '',
          projectId: item.projectId || item.bookingInfo?.particular?.projectId || '',
          unit: (item.bookingInfo?.particular?.inventoryDetail || item.inventoryName || '').trim(),
          bookingId: item.bookingId || '',
          bookingIdNo: item.bookingIdNo || item.bookingInfo?.bookingId || '',
          _raw: item,
        }));
        setPaymentData(mappedData);
      } else {
        setPaymentData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to search payment data:', error);
      showAlert('Failed to search payment data. Please try again later.', 'error', 'Search Error');
      setPaymentData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Trigger search when any non-date filter changes
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (isResetting.current) {
      return;
    }
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedBooking,
    selectedAssociate,
    filters.project,
    filters.unitType,
    filters.unitNo,
    filters.includeTeamMembers,
  ]);

  // Filter data (client-side: only hide "Incentive Credit" rows — pagination is server-side)
  let filteredData = paymentData;

  // Hide "Incentive Credit" transactions from the frontend list only
  filteredData = filteredData.filter(
    (item) =>
      item.head?.trim().toLowerCase() !== 'incentive credit' &&
      item._raw?.paymentHead?.trim().toLowerCase() !== 'incentive credit',
  );



  const buildExportRows = (list) => {
    return list.map((row) => ({
      Date: row.date,
      'Upto Date': row.uptoDate,
      Associate: row.associate,
      'Associate Phone': row.associatePhone,
      'Payment Head': row.head,
      'Payable Amount (₹)': row.payableAmount,
      Project: row.project,
      Unit: row.unit,
    }));
  };

  const handleExportPdf = () => {
    const list = filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    if (!list.length) {
      showAlert('No payment data available in this page to export.');
      return;
    }
    const rows = buildExportRows(list);
    exportAsPDF({
      rows,
      title: 'Payment Associate List',
      fileNameBase: 'Payment_Associate_Export',
    });
  };

  const handleExportCsv = () => {
    const list = filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    if (!list.length) {
      showAlert('No payment data available in this page to export.');
      return;
    }
    const rows = buildExportRows(list);
    exportAsCSV({ rows, fileNameBase: 'Payment_Associate_Export' });
  };

  const handleExportExcel = () => {
    const list = filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    if (!list.length) {
      showAlert('No payment data available in this page to export.');
      return;
    }
    const rows = buildExportRows(list);
    exportAsExcel({
      rows,
      sheetName: 'Payments',
      fileNameBase: 'Payment_Associate_Export',
    });
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant='h4'
            sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}
          >
            Payment Details
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            View and manage payment details for associates
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <ExportButton
            onExportPdf={handleExportPdf}
            onExportCsv={handleExportCsv}
            onExportExcel={handleExportExcel}
            variant='outlined'
            sx={{
              px: 2.5,
              py: 1.2,
              fontSize: '15px',
            }}
          />
          <RefreshButton
            onClick={fetchPaymentData}
            loading={loading}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 44 },
            }}
          />
        </Box>
      </Box>

      {/* Summary Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    Total Commission
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 700, color: '#4caf50' }}
                  >
                    ₹{apiSummary.totalCommission.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Gross Commission Amount
                  </Typography>
                </Box>
                <CurrencyRupeeIcon
                  sx={{ fontSize: 40, color: '#4caf50', opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    Payable Commission
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 700, color: '#1976d2' }}
                  >
                    ₹{apiSummary.totalPayableCommission.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Net Payable Amount
                  </Typography>
                </Box>
                <TrendingUpIcon
                  sx={{ fontSize: 40, color: '#1976d2', opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    Paid Commission
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 700, color: '#f57c00' }}
                  >
                    ₹{apiSummary.totalPaidCommission.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Amount Disbursed
                  </Typography>
                </Box>
                <ReceiptIcon
                  sx={{ fontSize: 40, color: '#f57c00', opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    Due Commission
                  </Typography>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 700, color: '#9c27b0' }}
                  >
                    ₹{apiSummary.dueCommission.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Pending to Disburse
                  </Typography>
                </Box>
                <AccountBalanceIcon
                  sx={{ fontSize: 40, color: '#9c27b0', opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Section */}
      <Card
        sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}
      >
        <CardContent>
          <Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>
            Search Section
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size='small'
                type='date'
                label='From Date'
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size='small'
                type='date'
                label='To Date'
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size='small'>
                <InputLabel>Unit Type</InputLabel>
                <Select
                  value={filters.unitType}
                  label='Unit Type'
                  onChange={(e) =>
                    handleFilterChange('unitType', e.target.value)
                  }
                  disabled={loadingUnitTypes}
                >
                  <MenuItem value=''>
                    <em>Select</em>
                  </MenuItem>
                  {unitTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {loadingUnitTypes && (
                  <CircularProgress
                    size={20}
                    sx={{ position: 'absolute', right: 30, top: '25%' }}
                  />
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size='small'
                value={selectedProject}
                options={projectOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.projectName || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  return (
                    (option.id || option._id) ===
                    (value.id || value._id || value)
                  );
                }}
                loading={loadingProjects}
                inputValue={projectSearchInput}
                onInputChange={(event, newInputValue) => {
                  setProjectSearchInput(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setSelectedProject(newValue);
                  handleFilterChange(
                    'project',
                    newValue ? newValue.id || newValue._id : '',
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Project'
                    placeholder='Search Project'
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProjects ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 10 &&
                      !loadingProjects &&
                      hasMoreProjects
                    ) {
                      fetchProjects(projectSearchInput, projectPage + 1, true);
                    }
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size='small'
                value={selectedUnit}
                options={unitOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.unitNo || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  return (
                    (option.id || option._id) ===
                    (value.id || value._id || value)
                  );
                }}
                loading={loadingUnits}
                inputValue={unitSearchInput}
                onInputChange={(event, newInputValue) => {
                  setUnitSearchInput(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setSelectedUnit(newValue);
                  handleFilterChange('unitNo', newValue ? newValue.unitNo : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Unit No'
                    placeholder='Search Unit'
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUnits ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                        listboxNode.scrollHeight - 10 &&
                      !loadingUnits &&
                      hasMoreUnits
                    ) {
                      fetchUnits(unitSearchInput, unitPage + 1, true);
                    }
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size='small'
                options={bookingOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return (
                    option.displayLabel || `Booking ${option.bookingId || ''}`
                  );
                }}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                loading={loadingBookings}
                inputValue={bookingSearchInput}
                onInputChange={(event, newInputValue, reason) => {
                  // If booking is selected and input is the display label, convert to booking ID
                  if (
                    selectedBooking &&
                    selectedBooking.displayLabel &&
                    newInputValue === selectedBooking.displayLabel
                  ) {
                    setBookingSearchInput(
                      selectedBooking.bookingId?.toString() || '',
                    );
                    return; // Don't trigger API call
                  }

                  // Update input value
                  setBookingSearchInput(newInputValue);

                  // If booking input is cleared, reset the flag
                  if (!newInputValue || newInputValue.trim().length === 0) {
                    setBookingSearchedFirst(false);
                  }
                }}
                value={selectedBooking}
                onChange={(event, newValue) => {
                  setSelectedBooking(newValue);
                  handleFilterChange(
                    'bookingNo',
                    newValue ? newValue.bookingId : '',
                  );
                  // Set input to booking ID when booking is selected
                  if (newValue) {
                    setBookingSearchInput(newValue.bookingId?.toString() || '');
                  } else {
                    // Clear booking selection when cleared
                    setBookingSearchInput('');
                    setBookingSearchedFirst(false);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Booking No'
                    placeholder={
                      selectedAssociate
                        ? 'Select from bookings'
                        : 'Type Booking No'
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingBookings ? (
                            <CircularProgress color='inherit' size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  loadingBookings
                    ? 'Loading bookings...'
                    : selectedAssociate
                      ? 'No bookings found for this associate'
                      : 'Type booking number to search'
                }
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Typography variant='body2'>
                      {option.displayLabel}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={associateOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name || '';
                }}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                loading={loadingAssociates}
                inputValue={associateSearchInput}
                onInputChange={(event, newInputValue) => {
                  setAssociateSearchInput(newInputValue);
                }}
                onOpen={() => {
                  // CRITICAL: Don't call associate API if booking was searched first
                  // (associate is already populated from booking API response)
                  if (bookingSearchedFirst) {
                    return; // Don't call associate API at all
                  }
                  // Load associates when dropdown is opened
                  if (!associatesLoaded && !loadingAssociates) {
                    fetchAssociates('');
                  }
                }}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  handleFilterChange(
                    'associateName',
                    newValue ? newValue.name : '',
                  );
                  // Clear booking selection when associate changes
                  if (newValue) {
                    setSelectedBooking(null);
                    setBookingSearchInput('');
                    handleFilterChange('bookingNo', '');
                    // Reset bookingSearchedFirst flag when associate is manually selected
                    setBookingSearchedFirst(false);
                  } else {
                    // Associate cleared
                    setSelectedBooking(null);
                    setBookingSearchInput('');
                    handleFilterChange('bookingNo', '');
                    setBookingOptions([]);
                    setBookingSearchedFirst(false);
                  }
                }}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Typography variant='body2'>
                      {option.name}
                      {option.phone && (
                        <Typography
                          component='span'
                          variant='caption'
                          color='text.secondary'
                          sx={{ ml: 1 }}
                        >
                          - {option.phone}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Associate Name'
                    size='small'
                    placeholder='Click to search Associate'
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingAssociates ? (
                            <CircularProgress color='inherit' size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  loadingAssociates
                    ? 'Loading associates...'
                    : 'No associates found'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size='small' required>
                <InputLabel>Include Team Members</InputLabel>
                <Select
                  value={filters.includeTeamMembers}
                  label='Include Team Members'
                  onChange={(e) =>
                    handleFilterChange('includeTeamMembers', e.target.value)
                  }
                >
                  {includeTeamOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant='outlined'
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 2,
                    height: '40px',
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 2,
                    height: '40px',
                  }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant='h6' sx={{ fontWeight: 700, mb: 2 }}>
            Payment Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Upto Date
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Booking No.
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Associate
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Associate Phone
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Payment Head
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '14px',
                      textAlign: 'right',
                    }}
                  >
                    Payable Amount (₹)
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Project
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}
                  >
                    Unit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 2 }}
                      >
                        Loading payment details...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant='body1' color='text.secondary'>
                        No payment details found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                          '&:hover': { backgroundColor: '#f0f7ff !important' },
                        }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.date}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.uptoDate}
                        </TableCell>
                        <TableCell>
                          {row.bookingIdNo ? (
                            <Box
                              component='span'
                              onClick={() =>
                                row.bookingId &&
                                history.push(
                                  `/app/booking/BookingDetails/${row.bookingId}`,
                                )
                              }
                              sx={{
                                fontWeight: 700,
                                color: '#536DFE',
                                cursor: row.bookingId ? 'pointer' : 'default',
                                textDecoration: row.bookingId
                                  ? 'underline'
                                  : 'none',
                                '&:hover': row.bookingId
                                  ? { color: '#3F51B5' }
                                  : {},
                              }}
                            >
                              {row.bookingIdNo}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.associate}
                        </TableCell>
                        <TableCell>{row.associatePhone}</TableCell>
                        <TableCell>
                          {row.head && (
                            <Box
                              component='span'
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '12px',
                                fontWeight: 600,
                                backgroundColor:
                                  row.head === 'Advance Payment'
                                    ? '#e3f2fd'
                                    : '#e8f5e9',
                                color:
                                  row.head === 'Advance Payment'
                                    ? '#1976d2'
                                    : '#2e7d32',
                              }}
                            >
                              {row.head}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: '#000',
                            textAlign: 'right',
                            fontFamily: 'monospace',
                          }}
                        >
                          ₹{row.payableAmount.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>{row.project}</TableCell>
                        <TableCell>{row.unit}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Server-side Pagination */}
          <TablePagination
            component='div'
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </CardContent>
      </Card>

      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </Box>
  );
};

export default PaymentAssociateList;
